<template>
	<teleport to="head">
		<component :is="'script'" type="application/ld+json">
			{{ meta.destinationsGuide }}
		</component>
	</teleport>
	<PageTitle
		text="Destinationer i Europa"
		textColor="black"
		borderColor="black"
		maxHeight="60vh"
		backgroundImageClass="background-countries"
	/>
	<ContentWrapper :showBreadcrumbs="true">
		<Breadcrumbs />
		<div class="card_text_and_image_wrapper">
			<TextBlock header="Destinationer ditt interrailkort når">
				Ditt interrailkort tar dig till 33 av Europas länder. Vi har tagit fram
				guider för många av dessa länder för att ge dig inspiration inför din
				tågluff. De flesta stora städer i Europa går lätt att ta sig till med
				tåg, men du kan också uppleva stränder, hav, vildmark, natur och mycket
				mer längre från civilisationen. Vi har alltid tillgängligheten med i
				tanken när våra guider utformats.
			</TextBlock>
			<TextBlock header="Att resa med tåg i Europa">
				Matnyttig information om hur du bäst reser med tåg i Europa finner du i
				vår guide
				<a href="/guider/tagluffning-for-nyborjare">
					Tågluffning för nybörjare</a
				>. Alla landsspecifika råd finns att funna under varje lands guide.
				Huruvida du kan resa med mer än bara väska, till exempel rullstol eller
				cykel, kan du läsa om i
				<a href="/guider/med-pa-resan"> Vad får jag med på resan?</a>.
			</TextBlock>
			<GridCard title="Destinationer i Europa" :items="items" />
		</div>
	</ContentWrapper>
</template>

<script lang="ts" setup>
import TextBlock from "../../blocks/TextBlock.vue";
import ContentWrapper from "../../generics/ContentWrapper.vue";
import PageTitle from "../../blocks/PageTitle.vue";
import CustomPic from "../../generics/CustomPic.vue";
import meta from "@/data/meta.json";
import GridCard from "../GridCard.vue";
import ListCard from "../ListCard.vue";
import { useRoute } from "vue-router";
import { computed } from "vue";
import Breadcrumbs from "@/components/generics/BreadCrumbs.vue";

const route = useRoute();

const basePath = computed(() => route.path.replace(/\/$/, ""));

const items = computed(() => [
	{
		title: "Belgien",
		image: "🇧🇪",
		description:
			"Mäktiga slott, belgisk choklad och öl i världsklass i hjärtat av Europa.",
		link: `${basePath.value}/belgien`,
	},
	{
		title: "Bosnien-Hercegovina",
		image: "🇧🇦",
		description:
			"Upptäck oslagbar natur, orientaliska influenser och historiska städer.",
		link: `${basePath.value}/bosnien-hercegovina`,
	},
	{
		title: "Bulgarien",
		image: "🇧🇬",
		description:
			"Från Svarta havets stränder till antika ruiner och bergsbyar.",
		link: `${basePath.value}/bulgarien`,
	},
	{
		title: "Danmark",
		image: "🇩🇰",
		description: "Kustnära charm, hygge och cykelvänliga storstäder.",
		link: `${basePath.value}/danmark`,
	},
	{
		title: "Estland",
		image: "🇪🇪",
		description: "Digitalt land med medeltida städer och tyst vildmark.",
		link: `${basePath.value}/estland`,
	},
	{
		title: "Finland",
		image: "🇫🇮",
		description: "Tusen sjöars land med bastu, skogar och norrsken.",
		link: `${basePath.value}/finland`,
	},
	{
		title: "Frankrike",
		image: "🇫🇷",
		description: "Renässansens vagga - perfekt för konst, vin och landsbygd.",
		link: `${basePath.value}/frankrike`,
	},
	{
		title: "Grekland",
		image: "🇬🇷",
		description: "Egeiska öar, antika tempel och det grekiska kökets pärlor.",
		link: `${basePath.value}/grekland`,
	},
	{
		title: "Irland",
		image: "🇮🇪",
		description: "Gröna fält, pubkultur och dramatiska klippkuster.",
		link: `${basePath.value}/irland`,
	},
	{
		title: "Italien",
		image: "🇮🇹",
		description:
			"Kultur, pasta, kustbyar och renässans – la dolce vita väntar.",
		link: `${basePath.value}/italien`,
	},
	{
		title: "Kroatien",
		image: "🇭🇷",
		description:
			"Adriatiska havets pärla med gamla städer och kristallklart vatten.",
		link: `${basePath.value}/kroatien`,
	},
	{
		title: "Lettland",
		image: "🇱🇻",
		description: "Oupptäckt pärla med kuststäder, skogar och Baltisk charm.",
		link: `${basePath.value}/lettland`,
	},
	{
		title: "Litauen",
		image: "🇱🇹",
		description: "Medeltida städer, sanddyner och vacker natur i öst.",
		link: `${basePath.value}/litauen`,
	},
	{
		title: "Luxemburg",
		image: "🇱🇺",
		description: "Litet land med stora upplevelser – slott, skogar och lyx.",
		link: `${basePath.value}/luxemburg`,
	},
	{
		title: "Makedonien",
		image: "🇲🇰",
		description: "Balkansk mystik med sjöar, kyrkor och bergskedjor.",
		link: `${basePath.value}/makedonien`,
	},
	{
		title: "Montenegro",
		image: "🇲🇪",
		description: "Liten kustnation med storslagen natur och gamla städer.",
		link: `${basePath.value}/montenegro`,
	},
	{
		title: "Nederländerna",
		image: "🇳🇱",
		description: "Cykelvänliga städer, tulpanfält och charmiga kanaler.",
		link: `${basePath.value}/nederlanderna`,
	},
	{
		title: "Norge",
		image: "🇳🇴",
		description: "Fjordar, tåg genom bergen och storslagen vildmark.",
		link: `${basePath.value}/norge`,
	},
	{
		title: "Polen",
		image: "🇵🇱",
		description: "Historiska städer, vacker kust och tät skog.",
		link: `${basePath.value}/polen`,
	},
	{
		title: "Portugal",
		image: "🇵🇹",
		description: "Surf, stadsliv och färgglada gränder med fado.",
		link: `${basePath.value}/portugal`,
	},
	{
		title: "Rumänien",
		image: "🇷🇴",
		description: "Drakulalegender, karpatiska berg och färgstarka byar.",
		link: `${basePath.value}/rumänien`,
	},
	{
		title: "Schweiz",
		image: "🇨🇭",
		description: "Tåg genom alperna, choklad och vykortsvyer.",
		link: `${basePath.value}/schweiz`,
	},
	{
		title: "Serbien",
		image: "🇷🇸",
		description: "Levande nattliv, historia och flodnära storstäder.",
		link: `${basePath.value}/serbien`,
	},
	{
		title: "Slovakien",
		image: "🇸🇰",
		description: "Underskattad pärla med grottor, slott och naturreservat.",
		link: `${basePath.value}/slovakien`,
	},
	{
		title: "Slovenien",
		image: "🇸🇮",
		description: "Grönskande landskap, sjöar och grottor i Alpernas famn.",
		link: `${basePath.value}/slovenien`,
	},
	{
		title: "Spanien",
		image: "🇪🇸",
		description: "Stränder, tapas, berg och ett rikt kulturarv.",
		link: `${basePath.value}/spanien`,
	},
	{
		title: "Storbritannien",
		image: "🇬🇧",
		description: "Pubkultur, tågresor i högland och drottninglig charm.",
		link: `${basePath.value}/storbritannien`,
	},
	{
		title: "Tjeckien",
		image: "🇨🇿",
		description: "Sagostäder, billig öl och klassisk arkitektur.",
		link: `${basePath.value}/tjeckien`,
	},
	{
		title: "Turkiet",
		image: "🇹🇷",
		description: "Mellan öst och väst – marknader, moskéer och medelhavskust.",
		link: `${basePath.value}/turkiet`,
	},
	{
		title: "Tyskland",
		image: "🇩🇪",
		description: "Historiska städer, öltradition och effektiv tågtrafik.",
		link: `${basePath.value}/tyskland`,
	},
	{
		title: "Ungern",
		image: "🇭🇺",
		description: "Termalbad, pampiga broar och Donauutsikt i Budapest.",
		link: `${basePath.value}/ungern`,
	},
	{
		title: "Österrike",
		image: "🇦🇹",
		description: "Klassisk musik, alper och eleganta stadspromenader.",
		link: `${basePath.value}/osterrike`,
	},
]);
</script>

<style scoped>
.card_text_and_image_wrapper {
	display: flex;
	flex-direction: column;
	row-gap: 2rem;
}

.email {
	border: 1px solid lightgrey;
	padding: 2rem 1rem;
}
</style>
